import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FiTool, FiSettings, FiZap, FiBattery,
  FiThermometer, FiDisc, FiActivity, FiCheckCircle, FiPhone
} from 'react-icons/fi';
import { serviceCategories } from '../../../utils/htmlToReact';
import RelatedInventory from '../../../components/RelatedInventory';
import PageHero from '../../../components/page/PageHero';
import SectionHeading from '../../../components/page/SectionHeading';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6 },
};

const ServicesIndex = () => {
  const allServices = [
    {
      icon: FiTool,
      title: 'European Car Repair',
      description: 'Expert repair and maintenance for BMW, Mercedes, Audi, Porsche, and more',
      link: '/services/european-car-repair',
    },
    {
      icon: FiSettings,
      title: 'Factory Maintenance',
      description: 'Keep your warranty valid with scheduled maintenance',
      link: '/services/factory-maintenance',
    },
    {
      icon: FiDisc,
      title: 'Suspension Repair',
      description: 'Shocks, struts, alignment for a smooth ride',
      link: '/services/suspension-repair',
    },
    {
      icon: FiZap,
      title: 'Transmission Repair',
      description: 'Expert transmission diagnosis and repair services',
      link: '/services/transmission-repair',
    },
    {
      icon: FiThermometer,
      title: 'Radiator & Cooling',
      description: 'Cooling system repair to prevent overheating',
      link: '/services/radiator-repair',
    },
    {
      icon: FiBattery,
      title: 'Performance Upgrades',
      description: 'Tuning, exhaust, turbos - make real power',
      link: '/services/performance-upgrades',
    },
    {
      icon: FiActivity,
      title: 'Vehicle Diagnostics',
      description: 'Advanced diagnostic for check engine lights and issues',
      link: '/services/vehicle-diagnostics',
    },
  ];

  const whyChooseUs = [
    'European Car Specialists',
    'Full-Service Repair Shop',
    'Latest Diagnostic Equipment',
    '1 Year/12,000 Mile Warranty',
    'Quality OEM Parts',
    'Transparent Pricing',
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-black"
    >
      <Helmet>
        <title>Auto Repair Services | European Car Specialists | Paradise-Performance</title>
        <meta name="description" content="Complete auto repair services in Paradise, TX. European car specialists with expert technicians, quality parts, and warranty on all repairs." />
      </Helmet>

      {/* Hero Section */}
      <PageHero
        eyebrow="Auto Repair Services"
        title="Complete Auto Repair Services"
        subtitle="European specialists running a full-service repair shop — dealer quality, not dealer prices."
      >
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-xl bg-red-600 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-red-700"
          >
            Schedule Service
          </Link>
          <a
            href="tel:8173822316"
            className="inline-flex items-center justify-center rounded-xl border border-white/20 px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-white hover:text-black"
          >
            <FiPhone className="mr-2 h-5 w-5" />
            Call (817) 382-2316
          </a>
        </div>
      </PageHero>

      {/* Main Content */}
      <div className="bg-black">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Services Grid */}
          <motion.section {...fadeInUp} className="py-20 md:py-24">
            <SectionHeading
              eyebrow="What We Do"
              title="Our Services"
              subtitle="From routine maintenance to complex repairs, we handle it all."
            />

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {allServices.map((service, index) => (
                <Link
                  key={index}
                  to={service.link}
                  className="group block h-full rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8 transition-all duration-300 hover:border-red-500/30 hover:bg-white/[0.05]"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-red-600/20 bg-red-600/10 transition-colors group-hover:bg-red-600/20">
                    <service.icon className="h-7 w-7 text-red-500" />
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-white transition-colors group-hover:text-red-400">
                    {service.title}
                  </h3>
                  <p className="leading-relaxed text-gray-400">
                    {service.description}
                  </p>
                </Link>
              ))}
            </div>
          </motion.section>

          {/* European Specialists Section */}
          <motion.section {...fadeInUp} className="pb-20 md:pb-24">
            <SectionHeading
              eyebrow="Marques We Know"
              title="European Car Specialists"
              subtitle="Factory-level expertise across the brands we live and breathe."
            />

            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
              {serviceCategories.europeanRepair.brands.map((brand, index) => (
                <Link
                  key={index}
                  to={brand.path}
                  className="group rounded-2xl border border-white/[0.06] bg-white/[0.03] p-6 text-center transition-all duration-300 hover:border-red-500/30 hover:bg-white/[0.05]"
                >
                  <h3 className="font-semibold text-white transition-colors group-hover:text-red-400">
                    {brand.name}
                  </h3>
                </Link>
              ))}
            </div>
          </motion.section>

          {/* Why Choose Us */}
          <motion.section {...fadeInUp} className="pb-20 md:pb-24">
            <SectionHeading
              eyebrow="The Difference"
              title="Why Choose Paradise-Performance?"
            />

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {whyChooseUs.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center rounded-2xl border border-white/[0.06] bg-white/[0.03] p-6"
                >
                  <FiCheckCircle className="mr-4 h-6 w-6 flex-shrink-0 text-red-500" />
                  <span className="text-lg text-gray-200">{feature}</span>
                </div>
              ))}
            </div>
          </motion.section>

          {/* CTA Section */}
          <motion.section {...fadeInUp} className="pb-20 md:pb-24">
            <div className="overflow-hidden rounded-3xl border border-red-500/20 bg-gradient-to-br from-red-600/15 to-white/[0.02] p-10 text-center md:p-14">
              <h2 className="mb-4 text-3xl font-bold tracking-tight text-white md:text-4xl">
                Ready to Schedule Your Service?
              </h2>
              <p className="mx-auto mb-8 max-w-2xl text-lg text-gray-300">
                Expert technicians, quality parts, and guaranteed work.
              </p>
              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-xl bg-red-600 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-red-700"
                >
                  Schedule Service Online
                </Link>
                <a
                  href="tel:8173822316"
                  className="inline-flex items-center justify-center rounded-xl border border-white/20 px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-white hover:text-black"
                >
                  <FiPhone className="mr-2 h-5 w-5" />
                  (817) 382-2316
                </a>
              </div>
            </div>
          </motion.section>

          {/* Vehicle Inventory Links */}
          <div className="pb-20 md:pb-24">
            <RelatedInventory limit={4} title="Vehicles For Sale" theme="dark" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ServicesIndex;
